import type { ConversationSummary, LiveHint } from "@beacon/core/chat"

/*
 * What a live hint means for what the web app holds. A hint says only that a conversation changed
 * and how; whatever changed is read again over HTTP, through the audience check.
 */

/**
 * What to do on a hint: forget the conversation, read its view (and who is in it), read the
 * Member's list (which carries each conversation's unread count), read what is unread everywhere
 * (`unreadsQuery`), read the threads they follow, read the messages that mentioned them, read
 * their attention (`attentionQuery`; a mute changes what the unread summary counts), read their
 * Beacon inbox.
 */
export type HintEffect = {
  forget: boolean
  refreshView: boolean
  refreshList: boolean
  refreshUnreads: boolean
  refreshThreads: boolean
  refreshMentions: boolean
  refreshAttention: boolean
  refreshInbox: boolean
}

const aboutWhoIsIn = new Set([
  "participant-joined",
  "participant-left",
  "manager-added",
  "manager-removed",
  "channel-archived",
  "channel-unarchived",
])

/**
 * A hint's effect, given the Member's list of conversations as held (none when not read yet):
 * a revoked conversation is forgotten; one whose people, managers or archiving changed is read
 * again with the list; one whose details (description, Project) changed is read again alone, as
 * the list shows none of them. A message posted to or deleted from a joined conversation changes
 * what is unread in it (and moves a direct message up the list), as does a message posted to one
 * the list doesn't hold yet (a direct message's first); one in a thread changes the threads the
 * Member follows instead. Edits, reactions and link previews are no activity. Another of the
 * Member's devices reading (`read`) reads their read state again, mentions too (`mentions`, when
 * they marked mentions read), attention too (`attention`). Their Beacon inbox is read again when
 * it changed (`inbox`: a work notice kept, entries read, a choice made). A message that mentions
 * the Member (`mentioned`), posted, edited or deleted, reads their mentions again, wherever it is.
 */
export function effectOf(
  hint: LiveHint,
  mine: readonly ConversationSummary[] | undefined,
): HintEffect {
  const effect: HintEffect = {
    forget: false,
    refreshView: false,
    refreshList: false,
    refreshUnreads: false,
    refreshThreads: false,
    refreshMentions: false,
    refreshAttention: false,
    refreshInbox: false,
  }
  if (hint.kind === "revoked") return { ...effect, forget: true }
  if (hint.kind === "read")
    return hint.thread === undefined
      ? { ...effect, refreshList: true, refreshUnreads: true, refreshMentions: true }
      : { ...effect, refreshUnreads: true, refreshThreads: true, refreshMentions: true }
  if (hint.kind === "mentions") return { ...effect, refreshUnreads: true, refreshMentions: true }
  if (hint.kind === "attention") return { ...effect, refreshUnreads: true, refreshAttention: true }
  if (hint.kind === "inbox") return { ...effect, refreshInbox: true }
  if (hint.kind !== "changed") return effect
  const mentioned =
    hint.mentioned === true &&
    ["message-posted", "message-edited", "message-deleted"].includes(hint.change)
  const activity = activityOf(hint, mine, effect)
  return mentioned ? { ...activity, refreshUnreads: true, refreshMentions: true } : activity
}

/** A `changed` hint's effect on the conversation and the threads it holds, mentions aside. */
function activityOf(
  hint: Extract<LiveHint, { kind: "changed" }>,
  mine: readonly ConversationSummary[] | undefined,
  effect: HintEffect,
): HintEffect {
  if (aboutWhoIsIn.has(hint.change)) return { ...effect, refreshView: true, refreshList: true }
  if (hint.change === "details-changed") return { ...effect, refreshView: true }
  if (hint.change !== "message-posted" && hint.change !== "message-deleted") return effect
  if (hint.thread !== undefined) return { ...effect, refreshUnreads: true, refreshThreads: true }
  if (!mine) return effect
  const held = mine.some(({ id }) => id === hint.conversationId)
  if (!held && hint.change !== "message-posted") return effect
  return { ...effect, refreshList: true, refreshUnreads: true }
}

/**
 * Whether a timeline of `conversationId` caught up to `cursor` has anything to read for a hint:
 * the conversation's own, any later change to it; a `thread`'s, a later change in that thread, or
 * an edit or deletion of a message in none (its message, before anyone replied).
 */
export function needsCatchUp(
  hint: LiveHint,
  conversationId: string,
  cursor: number,
  thread?: string,
) {
  if (hint.kind !== "changed" || hint.conversationId !== conversationId || hint.seq <= cursor)
    return false
  if (thread === undefined) return true
  if (hint.thread !== undefined) return hint.thread === thread
  return hint.change === "message-edited" || hint.change === "message-deleted"
}
